import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  setDoc,
  updateDoc,
  doc,
  onSnapshot,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, storage } from "../firebaseConfig";
import { getAuth } from "firebase/auth";
import { useAuth } from "./AuthContext";

const HAZIR_BETON_SITE_COLLECTIONS = [
  "hazirbeton_zone_uretim",
  "hazirbeton_mikser_sevkiyat",
  "hazirbeton_pompa_hakedis",
];
const normalizeAliasId = (name) => {
  return (name || "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const AddIssue = () => {
  const [targetType, setTargetType] = useState("vehicle");
  const [plates, setPlates] = useState([]);
  const [platesLoading, setPlatesLoading] = useState(true);
  const [selectedPlate, setSelectedPlate] = useState("");
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [facilitiesLoaded, setFacilitiesLoaded] = useState(false);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [selectedFacilityName, setSelectedFacilityName] = useState("");
  const [siteOptions, setSiteOptions] = useState([]);
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [selectedSiteName, setSelectedSiteName] = useState("");
  const [selectedMatchSiteName, setSelectedMatchSiteName] = useState("");
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
  const [highlightedSiteIndex, setHighlightedSiteIndex] = useState(0);
  const [targetPhotos, setTargetPhotos] = useState([]);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [photoUploadStatus, setPhotoUploadStatus] = useState("");
  const [selectedIssues, setSelectedIssues] = useState([{ type: "", suggestions: [] }]);
  const [existingIssues, setExistingIssues] = useState([]);
  const [openTargetIssues, setOpenTargetIssues] = useState([]);
  const [openTargetSearch, setOpenTargetSearch] = useState("");
  const [issueTitleCache, setIssueTitleCache] = useState({});
  const [titleLoading, setTitleLoading] = useState({});
  const titleRequests = useRef({});
  const issueTitlesLoading = titleLoading[targetType] === true;
  const issueTitleIndex = useMemo(() => (issueTitleCache[targetType] || []).map(title => ({ title, search: title.toLowerCase() })), [issueTitleCache, targetType]);
  const suggestionsFor = item => {
    if (!item.suggestionsOpen || !item.type) return [];
    const needle = item.type.toLowerCase(), matches = [];
    for (const entry of issueTitleIndex) {
      if (entry.search.includes(needle)) matches.push(entry.title);
      if (matches.length === 40) break;
    }
    return matches;
  };

  const auth = getAuth();
  const { user: userCtx, role } = useAuth();
  const normalizedRole = (role || "").toLowerCase();
  const canResolve = ["oluşturucu", "olusturucu", "oluÅŸturucu", "creator"].includes(normalizedRole);

  useEffect(() => {
    const fetchPlates = async () => {
      try {
        setPlatesLoading(true);
        const snapshot = await getDocs(collection(db, "plates"));
        const data = snapshot.docs.map((docSnap) => docSnap.data().plate).filter(Boolean).sort();
        setPlates(data);
      } finally {
        setPlatesLoading(false);
      }
    };
    fetchPlates();
  }, []);

  const fetchFacilities = async () => {
    if (facilitiesLoaded || facilitiesLoading) return;
    setFacilitiesLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "tesisler"), where("active", "==", true)));
      const facilities = snap.docs
        .map((docSnap) => {
          const data = docSnap.data() || {};
          return (data.tesisAdi || data.tesisAd || data.tesis || data.name || data.ad || data.title || docSnap.id || "").trim();
        })
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "tr"));
      setFacilityOptions([...new Set(facilities)]);
      setFacilitiesLoaded(true);
    } finally {
      setFacilitiesLoading(false);
    }
  };

  const fetchSites = async () => {
    if (sitesLoaded || sitesLoading) return;
    setSitesLoading(true);
    try {
      const siteMap = new Map();
      const addSite = (name, customerName = "", source = "hazir_beton", matchStatus = "linked") => {
        const cleanName = (name || "").trim();
        if (!cleanName) return;
        const key = cleanName.toLocaleLowerCase("tr-TR");
        if (!siteMap.has(key)) {
          siteMap.set(key, { name: cleanName, customerName: customerName || "", source, matchStatus });
        }
      };

      for (const colName of HAZIR_BETON_SITE_COLLECTIONS) {
        const snap = await getDocs(collection(db, colName));
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          addSite(data.santiyeAdi, data.musteriAdi, "hazir_beton", "linked");
        });
      }

      const aliasSnap = await getDocs(collection(db, "site_aliases"));
      aliasSnap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        addSite(
          data.manualName,
          data.matchedCustomerName || "",
          data.matchStatus === "linked" ? "alias" : "manual",
          data.matchStatus || "unlinked"
        );
      });

      setSiteOptions([...siteMap.values()].sort((a, b) => a.name.localeCompare(b.name, "tr")));
      setSitesLoaded(true);
    } finally {
      setSitesLoading(false);
    }
  };

  useEffect(() => {
    if (targetType === "site") fetchSites();
    if (targetType === "facility") fetchFacilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType]);

  useEffect(() => {
    setExistingIssues([]);
    if (targetType === "vehicle" && !selectedPlate) return;
    if (targetType === "site" && !selectedSiteName.trim()) return;
    if (targetType === "facility" && !selectedFacilityName.trim()) return;

    const targetQuery =
      targetType === "site"
        ? query(collection(db, "issues"), where("siteName", "==", selectedSiteName.trim()))
        : targetType === "facility"
          ? query(collection(db, "issues"), where("facilityName", "==", selectedFacilityName.trim()))
          : query(collection(db, "issues"), where("plate", "==", selectedPlate), where("resolved", "==", false));

    const unsubscribe = onSnapshot(targetQuery, (snapshot) => {
      const data = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter((item) => item.resolved === false);
      setExistingIssues(data);
    });
    return () => unsubscribe();
  }, [targetType, selectedPlate, selectedSiteName, selectedFacilityName]);

  useEffect(() => {
    setSelectedIssues([{ type: "", suggestions: [] }]);
    setExistingIssues([]);
    setTargetPhotos([]);
  }, [targetType]);

  useEffect(() => {
    setOpenTargetIssues([]);
    setOpenTargetSearch("");
    if (targetType === "vehicle") return undefined;

    const unsubscribe = onSnapshot(
      query(collection(db, "issues"), where("resolved", "==", false)),
      (snapshot) => {
        const grouped = new Map();
        snapshot.forEach((docSnap) => {
          const item = { id: docSnap.id, ...docSnap.data() };
          const issueTargetType = item.targetType || "vehicle";
          if (issueTargetType !== targetType) return;

          const targetName = targetType === "site"
            ? (item.siteName || item.matchedSiteName || "")
            : (item.facilityName || item.tesis || "");
          const cleanName = targetName.trim();
          if (!cleanName) return;

          const key = cleanName.toLocaleLowerCase("tr-TR");
          const current = grouped.get(key) || { name: cleanName, issues: [], unlinked: false };
          current.issues.push(item);
          if (targetType === "site" && item.siteMatchStatus !== "linked") current.unlinked = true;
          grouped.set(key, current);
        });

        setOpenTargetIssues(
          [...grouped.values()].sort((a, b) =>
            b.issues.length - a.issues.length || a.name.localeCompare(b.name, "tr")
          )
        );
      }
    );

    return () => unsubscribe();
  }, [targetType]);

  const getSelectedSiteOption = () => {
    const cleanName = selectedSiteName.trim();
    if (!cleanName) return null;
    return siteOptions.find((site) => site.name.toLocaleLowerCase("tr-TR") === cleanName.toLocaleLowerCase("tr-TR")) || null;
  };

  const filteredSiteOptions = useMemo(() => {
    const value = selectedSiteName.trim().toLocaleLowerCase("tr-TR");
    const filtered = value
      ? siteOptions.filter((site) => site.name.toLocaleLowerCase("tr-TR").includes(value))
      : siteOptions;
    return filtered.slice(0, 40);
  }, [selectedSiteName, siteOptions]);

  useEffect(() => {
    setHighlightedSiteIndex(0);
  }, [selectedSiteName, siteOptions]);

  const selectSiteOption = (site) => {
    if (!site) return;
    setSelectedSiteName(site.name);
    setSiteDropdownOpen(false);
  };

  const handleSiteKeyDown = (e) => {
    if (!siteDropdownOpen && ["ArrowDown", "ArrowUp"].includes(e.key)) {
      setSiteDropdownOpen(true);
      return;
    }
    if (!siteDropdownOpen || filteredSiteOptions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedSiteIndex((idx) => Math.min(idx + 1, filteredSiteOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedSiteIndex((idx) => Math.max(idx - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectSiteOption(filteredSiteOptions[highlightedSiteIndex]);
    } else if (e.key === "Escape") {
      setSiteDropdownOpen(false);
    }
  };

  const linkedSiteOptions = siteOptions.filter((site) => site.matchStatus === "linked");

  const filteredOpenTargetIssues = useMemo(() => {
    const needle = openTargetSearch.trim().toLocaleLowerCase("tr-TR");
    const list = needle
      ? openTargetIssues.filter((target) => target.name.toLocaleLowerCase("tr-TR").includes(needle))
      : openTargetIssues;
    return list.slice(0, 30);
  }, [openTargetIssues, openTargetSearch]);

  const selectOpenTarget = (targetName) => {
    if (targetType === "site") {
      setSelectedSiteName(targetName);
      setSiteDropdownOpen(false);
    } else if (targetType === "facility") {
      setSelectedFacilityName(targetName);
    }
  };

  const handleMatchSite = async () => {
    const user = userCtx || auth.currentUser;
    if (!user) return alert("Giris yapmadan islem yapilamaz");

    const manualName = selectedSiteName.trim();
    const matched = linkedSiteOptions.find((site) => site.name === selectedMatchSiteName);
    if (!manualName || !matched) return alert("Eslesecek santiye secin.");
    if (manualName === matched.name) return alert("Ayni santiye tekrar eslestirilemez.");

    const aliasId = normalizeAliasId(manualName);
    if (!aliasId) return alert("Santiye adi gecersiz.");

    await setDoc(doc(db, "site_aliases", aliasId), {
      manualName,
      matchedName: matched.name,
      matchedCustomerName: matched.customerName || "",
      matchStatus: "linked",
      source: "arac_takip",
      updatedAt: Timestamp.now(),
      updatedBy: user.email,
    }, { merge: true });

    const issueSnap = await getDocs(query(collection(db, "issues"), where("siteName", "==", manualName)));
    const batch = writeBatch(db);
    issueSnap.forEach((docSnap) => {
      batch.update(doc(db, "issues", docSnap.id), {
        siteMatchStatus: "linked",
        matchedSiteName: matched.name,
        matchedCustomerName: matched.customerName || "",
      });
    });
    await batch.commit();

    setSiteOptions((prev) => {
      const withoutManual = prev.filter((site) => site.name.toLocaleLowerCase("tr-TR") !== manualName.toLocaleLowerCase("tr-TR"));
      return [
        ...withoutManual,
        { name: manualName, customerName: matched.customerName || "", source: "alias", matchStatus: "linked" },
      ].sort((a, b) => a.name.localeCompare(b.name, "tr"));
    });
    setSelectedMatchSiteName("");
    alert("Santiye eslestirildi.");
  };

  const fetchIssueTitles = () => {
    const type = targetType;
    if (issueTitleCache[type]) return Promise.resolve(issueTitleCache[type]);
    if (titleRequests.current[type]) return titleRequests.current[type];
    setTitleLoading(prev => ({ ...prev, [type]: true }));
    // Legacy vehicle records may lack targetType; site/facility records always have it.
    const source = type === "vehicle" ? collection(db, "issues") : query(collection(db, "issues"), where("targetType", "==", type));
    const request = getDocs(source).then(snap => {
      const titlesSet = new Set();
      snap.forEach(docSnap => {
        const data = docSnap.data() || {};
        if ((data.targetType || "vehicle") !== type) return;
        const title = data.issue?.trim();
        if (title) titlesSet.add(title);
      });
      const titles = [...titlesSet];
      setIssueTitleCache(prev => ({ ...prev, [type]: [...new Set([...(prev[type] || []), ...titles])].sort((a, b) => a.localeCompare(b, "tr")) }));
      return titles;
    }).catch(error => {
      // Suggestions are optional: connection problems must never prevent typing/saving.
      console.warn("Issue title suggestions could not be loaded", error.code);
      return [];
    }).finally(() => {
      delete titleRequests.current[type];
      setTitleLoading(prev => ({ ...prev, [type]: false }));
    });
    titleRequests.current[type] = request;
    return request;
  };

  const handleAddIssueInput = () => {
    setSelectedIssues(prev => [...prev, { type: "", suggestions: [] }]);
  };

  const handleInputChange = (index, value) => {
    setSelectedIssues(prev => prev.map((item, i) => i === index ? { ...item, type: value, suggestionsOpen: true } : item));
    fetchIssueTitles();
  };

  const handleSelectSuggestion = (index, suggestion) => {
    setSelectedIssues(prev => prev.map((item, i) => i === index ? { ...item, type: suggestion, suggestionsOpen: false } : item));
  };

  const compressImageFile = (file, maxSize = 1600, quality = 0.75) => {
    if (!file.type.startsWith("image/")) return Promise.resolve(file);

    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        if (scale >= 1 && file.size <= 1200 * 1024) {
          resolve(file);
          return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const compressed = new File(
            [blob],
            file.name.replace(/\.[^.]+$/, "") + ".jpg",
            { type: "image/jpeg", lastModified: Date.now() }
          );
          resolve(compressed);
        }, "image/jpeg", quality);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
      };

      img.src = objectUrl;
    });
  };

  const uploadPhotoWithTimeout = (fileRef, file, onProgress, timeoutMs = 180000) => {
    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(fileRef, file);
      const timer = setTimeout(() => {
        task.cancel();
        reject(new Error("Fotograf yukleme zaman asimina ugradi."));
      }, timeoutMs);
      const startTimer = setTimeout(() => {
        task.cancel();
        reject(new Error("Firebase Storage yukleme istegi baslamadi. CORS, App Check veya Storage rules ayarlarini kontrol edin."));
      }, 20000);

      task.on(
        "state_changed",
        (snapshot) => {
          clearTimeout(startTimer);
          const progress = snapshot.totalBytes
            ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            : 0;
          if (onProgress) onProgress(progress);
        },
        (error) => {
          clearTimeout(timer);
          clearTimeout(startTimer);
          if (error?.code === "storage/unauthorized") {
            reject(new Error("Firebase Storage yazma izni yok. Storage rules kontrol edilmeli."));
            return;
          }
          if (error?.code === "storage/canceled") {
            reject(new Error("Fotograf yukleme iptal edildi veya zaman asimina ugradi."));
            return;
          }
          if (error?.code === "storage/unknown" || error?.message?.includes("ERR_FAILED")) {
            reject(new Error("Firebase Storage yukleme istegi engellendi. CORS, App Check veya Storage rules ayarlarini kontrol edin."));
            return;
          }
          reject(new Error(error?.message || "Firebase Storage yukleme hatasi."));
        },
        async () => {
          clearTimeout(timer);
          clearTimeout(startTimer);
          try {
            resolve(await getDownloadURL(task.snapshot.ref));
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const handleAddIssues = async () => {
    const user = userCtx || auth.currentUser;
    if (!user) return alert("Giris yapmadan islem yapilamaz");
    if (targetType === "vehicle" && !selectedPlate) return alert("Plaka secin.");
    if (targetType === "vehicle" && !plates.includes(selectedPlate)) return alert("Plaka listesinden gecerli bir plaka secin.");
    if (targetType === "site" && !selectedSiteName.trim()) return alert("Santiye adi yazin veya secin.");
    if (targetType === "facility" && !selectedFacilityName.trim()) return alert("Tesis secin.");
    if (targetType === "facility" && !facilityOptions.includes(selectedFacilityName)) return alert("Tesis listesinden gecerli bir tesis secin.");

    let plateData = {};
    let siteOption = null;

    if (targetType === "vehicle") {
      const plateQuery = query(collection(db, "plates"), where("plate", "==", selectedPlate));
      const plateSnap = await getDocs(plateQuery);
      plateData = plateSnap.docs[0]?.data() || {};
    } else if (targetType === "site") {
      siteOption = getSelectedSiteOption();
      if (!siteOption || siteOption.matchStatus !== "linked") {
        const aliasId = normalizeAliasId(selectedSiteName);
        if (aliasId) {
          await setDoc(doc(db, "site_aliases", aliasId), {
            manualName: selectedSiteName.trim(),
            matchStatus: "unlinked",
            source: "arac_takip",
            createdAt: Timestamp.now(),
            createdBy: user.email,
          }, { merge: true });
        }
      }
    }

    let photoUrls = [];
    if (targetType !== "vehicle" && targetPhotos.length > 0) {
      setPhotosUploading(true);
      setPhotoUploadStatus("");
      try {
        const targetName = targetType === "facility" ? selectedFacilityName : selectedSiteName;
        const cleanTarget = normalizeAliasId(targetName) || targetType;
        const uploadedUrls = [];
        for (let index = 0; index < targetPhotos.length; index += 1) {
          const originalFile = targetPhotos[index];
          setPhotoUploadStatus(`${index + 1}/${targetPhotos.length} fotograf hazirlaniyor...`);
          const file = await compressImageFile(originalFile);
          setPhotoUploadStatus(`${index + 1}/${targetPhotos.length} fotograf yukleniyor...`);
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
          const path = `${targetType}-issue-photos/${cleanTarget}/${Date.now()}-${index}-${safeName}`;
          const fileRef = ref(storage, path);
          const url = await uploadPhotoWithTimeout(fileRef, file, (progress) => {
            setPhotoUploadStatus(`${index + 1}/${targetPhotos.length} fotograf yukleniyor... %${progress}`);
          });
          uploadedUrls.push(url);
        }
        photoUrls = uploadedUrls;
      } catch (error) {
        console.error("Foto yukleme hatasi:", error);
        alert("Fotograf yuklenemedi: " + (error?.message || error));
        return;
      } finally {
        setPhotosUploading(false);
        setPhotoUploadStatus("");
      }
    }

    const addedTitles = [];
    for (let issue of selectedIssues) {
      const issueText = issue.type?.trim();
      if (!issueText) continue;
      addedTitles.push(issueText);

      const payload = {
        targetType,
        issue: issueText,
        date: Timestamp.now(),
        resolved: false,
        createdBy: user.email,
      };

      if (targetType === "vehicle") {
        Object.assign(payload, {
          plate: selectedPlate,
          tesis: plateData.tesis || "Belirtilmemis",
          aracCinsi: plateData.aracCinsi || "",
          model: plateData.model || "",
        });
      } else if (targetType === "site") {
        Object.assign(payload, {
          plate: "",
          siteName: selectedSiteName.trim(),
          siteSource: siteOption?.source || "manual",
          siteMatchStatus: siteOption?.matchStatus === "linked" ? "linked" : "unlinked",
          matchedSiteName: siteOption?.matchStatus === "linked" ? siteOption.name : "",
          matchedCustomerName: siteOption?.matchStatus === "linked" ? siteOption.customerName || "" : "",
          photoUrls,
          tesis: "Santiye",
          aracCinsi: "",
          model: "",
        });
      } else {
        Object.assign(payload, {
          plate: "",
          facilityName: selectedFacilityName.trim(),
          photoUrls,
          tesis: selectedFacilityName.trim(),
          aracCinsi: "",
          model: "",
        });
      }

      await addDoc(collection(db, "issues"), payload);
    }

    if (addedTitles.length > 0) {
      setIssueTitleCache((prev) => {
        const current = prev[targetType] || [];
        const next = [...new Set([...current, ...addedTitles])].sort((a, b) => a.localeCompare(b, "tr"));
        return { ...prev, [targetType]: next };
      });
    }

    alert("Eksiklik(ler) basariyla eklendi.");
    setSelectedIssues([{ type: "", suggestions: [] }]);
    setTargetPhotos([]);
  };

  const handleResolve = async (id) => {
    if (!canResolve) return alert("Bu islem icin yetkiniz yok.");
    const user = userCtx || auth.currentUser;
    if (!user) return alert("Giris yapmadan islem yapilamaz");

    await updateDoc(doc(db, "issues", id), {
      resolved: true,
      resolvedBy: user.email,
      resolvedAt: Timestamp.now(),
    });
  };

  const canShowIssueForm =
    (targetType === "vehicle" && selectedPlate) ||
    (targetType === "site" && selectedSiteName.trim()) ||
    (targetType === "facility" && selectedFacilityName.trim());

  return (
    <div style={{ padding: 20, maxWidth: 600, margin: "0 auto" }}>
      <h2 style={{ marginBottom: 20 }}>Eksiklik Ekle</h2>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button type="button" onClick={() => setTargetType("vehicle")} style={targetType === "vehicle" ? activeTargetBtn : targetBtn}>Arac</button>
        <button type="button" onClick={() => setTargetType("site")} style={targetType === "site" ? activeTargetBtn : targetBtn}>Santiye</button>
        <button type="button" onClick={() => setTargetType("facility")} style={targetType === "facility" ? activeTargetBtn : targetBtn}>Tesis</button>
      </div>

      {targetType === "vehicle" && (
        <>
          <label style={labelStyle}>Plaka Sec:</label>
          <input
            type="text"
            list="plate-options"
            value={selectedPlate}
            onChange={(e) => setSelectedPlate(e.target.value)}
            placeholder={platesLoading ? "Plakalar yukleniyor..." : "Plaka yazin veya secin"}
            style={inputStyle}
          />
          <datalist id="plate-options">
            {plates.map((p) => <option key={p} value={p} />)}
          </datalist>
        </>
      )}

      {targetType === "facility" && (
        <>
          <label style={labelStyle}>Tesis Sec:</label>
          {openTargetIssues.length > 0 && (
            <div style={openTargetBoxStyle}>
              <div style={openTargetHeaderStyle}>Acik eksikligi olan tesisler</div>
              <input
                type="text"
                value={openTargetSearch}
                onChange={(e) => setOpenTargetSearch(e.target.value)}
                placeholder="Tesis ara..."
                style={smallSearchStyle}
              />
              <div style={openTargetListStyle}>
                {filteredOpenTargetIssues.map((target) => (
                  <button
                    key={target.name}
                    type="button"
                    onClick={() => selectOpenTarget(target.name)}
                    style={selectedFacilityName === target.name ? activeOpenTargetChipStyle : openTargetChipStyle}
                  >
                    <span>{target.name}</span>
                    <b>{target.issues.length}</b>
                  </button>
                ))}
              </div>
            </div>
          )}
          <input
            type="text"
            list="facility-options"
            value={selectedFacilityName}
            onChange={(e) => setSelectedFacilityName(e.target.value)}
            onFocus={fetchFacilities}
            placeholder={facilitiesLoading ? "Tesisler yukleniyor..." : "Tesis yazin veya secin"}
            style={inputStyle}
          />
          <datalist id="facility-options">
            {facilityOptions.map((facility) => <option key={facility} value={facility} />)}
          </datalist>
          {facilitiesLoading && (
            <div style={loadingTextStyle}>Tesis listesi yukleniyor...</div>
          )}
          {!facilitiesLoading && facilitiesLoaded && facilityOptions.length === 0 && (
            <div style={loadingTextStyle}>Kayitli tesis bulunamadi.</div>
          )}
          <label style={labelStyle}>Fotograf Ekle:</label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => setTargetPhotos(Array.from(e.target.files || []))}
            style={fileInputStyle}
          />
          {targetPhotos.length > 0 && (
            <div style={loadingTextStyle}>{targetPhotos.length} fotograf secildi.</div>
          )}
        </>
      )}

      {targetType === "site" && (
        <>
          <label style={labelStyle}>Santiye Sec veya Yaz:</label>
          <div style={{ marginBottom: 10, position: "relative" }}>
          {openTargetIssues.length > 0 && (
            <div style={openTargetBoxStyle}>
              <div style={openTargetHeaderStyle}>Acik eksikligi olan santiyeler</div>
              <input
                type="text"
                value={openTargetSearch}
                onChange={(e) => setOpenTargetSearch(e.target.value)}
                placeholder="Santiye ara..."
                style={smallSearchStyle}
              />
              <div style={openTargetListStyle}>
                {filteredOpenTargetIssues.map((target) => (
                  <button
                    key={target.name}
                    type="button"
                    onClick={() => selectOpenTarget(target.name)}
                    style={selectedSiteName === target.name ? activeOpenTargetChipStyle : openTargetChipStyle}
                    title={target.unlinked ? "Baglanmamis santiye" : ""}
                  >
                    <span>{target.name}</span>
                    <b>{target.issues.length}</b>
                    {target.unlinked && <em>!</em>}
                  </button>
                ))}
              </div>
            </div>
          )}
            <input
              type="text"
              value={selectedSiteName}
              onChange={(e) => {
                setSelectedSiteName(e.target.value);
                setSiteDropdownOpen(true);
              }}
              onFocus={() => {
                fetchSites();
                setSiteDropdownOpen(true);
              }}
              onBlur={() => setTimeout(() => setSiteDropdownOpen(false), 120)}
              onKeyDown={handleSiteKeyDown}
              placeholder="Santiye adi"
              style={inputStyle}
            />
            {siteDropdownOpen && filteredSiteOptions.length > 0 && (
              <ul style={dropdownStyle}>
                {filteredSiteOptions.map((site, index) => {
                  const unlinked = site.matchStatus !== "linked";
                  const active = index === highlightedSiteIndex;
                  return (
                    <li
                      key={`${site.name}-${site.source}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectSiteOption(site);
                      }}
                      onMouseEnter={() => setHighlightedSiteIndex(index)}
                      style={{
                        ...dropdownItem,
                        background: active ? "#dbeafe" : unlinked ? "#fffbeb" : "#fff",
                        color: unlinked ? "#b45309" : "#0f172a",
                        fontWeight: unlinked ? 700 : 400,
                      }}
                    >
                      <span>{site.name}</span>
                      {unlinked && <span style={{ marginLeft: 8, fontSize: 12 }}>(baglanmamis)</span>}
                    </li>
                  );
                })}
              </ul>
            )}
            {sitesLoading && (
              <div style={loadingTextStyle}>Santiye listesi yukleniyor...</div>
            )}
            {!sitesLoading && sitesLoaded && siteOptions.length === 0 && (
              <div style={loadingTextStyle}>Kayitli santiye bulunamadi, yeni ad yazabilirsiniz.</div>
            )}
          </div>
          {selectedSiteName.trim() && getSelectedSiteOption()?.matchStatus !== "linked" && linkedSiteOptions.length > 0 && (
            <div style={matchBoxStyle}>
              <label style={labelStyle}>Hazir beton santiyesiyle eslestir:</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                <select value={selectedMatchSiteName} onChange={(e) => setSelectedMatchSiteName(e.target.value)} style={selectStyle}>
                  <option value="">-- Eslesecek santiyeyi sec --</option>
                  {linkedSiteOptions.map((site) => (
                    <option key={`${site.name}-${site.customerName}`} value={site.name}>
                      {site.name}{site.customerName ? ` / ${site.customerName}` : ""}
                    </option>
                  ))}
                </select>
                <button type="button" onClick={handleMatchSite} style={matchBtn}>Eslestir</button>
              </div>
            </div>
          )}
          <label style={labelStyle}>Fotograf Ekle:</label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => setTargetPhotos(Array.from(e.target.files || []))}
            style={fileInputStyle}
          />
          {targetPhotos.length > 0 && (
            <div style={loadingTextStyle}>{targetPhotos.length} fotograf secildi.</div>
          )}
        </>
      )}

      {canShowIssueForm && (
        <>
          <h4 style={sectionTitle}>Mevcut Eksiklikler</h4>
          {existingIssues.length === 0 ? (
            <div style={emptyIssueStyle}>Bu hedef icin acik eksiklik yok.</div>
          ) : (
            <div style={existingIssueListStyle}>
              {existingIssues.map((issue) => (
                <div key={issue.id} style={existingIssueCardStyle}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{issue.issue}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      {issue.date?.seconds ? new Date(issue.date.seconds * 1000).toLocaleDateString("tr-TR") : "-"}
                    </div>
                  </div>
                  {canResolve && (
                    <button onClick={() => handleResolve(issue.id)} style={resolveBtn}>Cozuldu</button>
                  )}
                </div>
              ))}
            </div>
          )}

          <h4 style={sectionTitle}>Yeni Eksiklik(ler)</h4>
          {selectedIssues.map((item, index) => { const suggestions = suggestionsFor(item); return (
            <div key={index} style={{ marginBottom: 12, position: "relative" }}>
              <input
                type="text"
                value={item.type}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onFocus={fetchIssueTitles}
                placeholder="Eksiklik yazin veya secin"
                style={inputStyle}
              />
              {suggestions.length > 0 && (
                <ul style={dropdownStyle}>
                  {suggestions.map((s, i) => (
                    <li key={i} onClick={() => handleSelectSuggestion(index, s)} style={dropdownItem}>
                      {s}
                    </li>
                  ))}
                </ul>
              )}
              {issueTitlesLoading && (
                <div style={loadingTextStyle}>Eksiklik onerileri yukleniyor...</div>
              )}
            </div>
          ); })}
          <button onClick={handleAddIssueInput} style={addBtn}>+ Eksiklik</button>
          <br /><br />
          <button onClick={handleAddIssues} style={submitBtn} disabled={photosUploading}>
            {photosUploading ? (photoUploadStatus || "Fotograflar yukleniyor...") : "Onayla"}
          </button>
        </>
      )}
    </div>
  );
};

const labelStyle = { fontWeight: "bold", marginBottom: 4, display: "block" };
const sectionTitle = { marginTop: 20, marginBottom: 10 };
const selectStyle = { width: "100%", padding: 10, marginBottom: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: "14px" };
const inputStyle = { width: "100%", padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: "14px" };
const dropdownStyle = { position: "absolute", background: "#fff", border: "1px solid #ccc", borderRadius: 6, marginTop: 4, maxHeight: 150, overflowY: "auto", width: "100%", zIndex: 999, listStyle: "none", padding: 0 };
const dropdownItem = { padding: "8px 10px", cursor: "pointer" };
const fileInputStyle = { width: "100%", marginBottom: 10, fontSize: "14px" };
const addBtn = { backgroundColor: "#0ea5e9", color: "white", border: "none", padding: "8px 12px", borderRadius: 6, cursor: "pointer", fontSize: "14px" };
const submitBtn = { backgroundColor: "#10b981", color: "white", border: "none", padding: "10px 20px", borderRadius: 6, cursor: "pointer", fontSize: "15px", fontWeight: "bold" };
const resolveBtn = { backgroundColor: "#22c55e", color: "white", border: "none", padding: "6px 10px", borderRadius: 6, marginLeft: 10, cursor: "pointer", fontSize: "13px" };
const targetBtn = { backgroundColor: "#f8fafc", color: "#0f172a", border: "1px solid #cbd5e1", padding: "8px 12px", borderRadius: 6, cursor: "pointer", fontSize: "14px" };
const activeTargetBtn = { ...targetBtn, backgroundColor: "#2563eb", border: "1px solid #2563eb", color: "#fff" };
const matchBoxStyle = { border: "1px solid #f59e0b", background: "#fffbeb", borderRadius: 6, padding: 10, marginBottom: 12 };
const matchBtn = { backgroundColor: "#f59e0b", color: "#111827", border: "none", padding: "8px 12px", borderRadius: 6, cursor: "pointer", fontSize: "14px", fontWeight: "bold" };
const loadingTextStyle = { marginTop: 6, fontSize: 12, color: "#475569" };
const openTargetBoxStyle = { border: "1px solid #cbd5e1", background: "#f8fafc", borderRadius: 8, padding: 10, marginBottom: 12 };
const openTargetHeaderStyle = { fontWeight: 700, marginBottom: 8, color: "#0f172a" };
const smallSearchStyle = { width: "100%", padding: 8, borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13, marginBottom: 8 };
const openTargetListStyle = { display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 140, overflowY: "auto" };
const openTargetChipStyle = { display: "inline-flex", alignItems: "center", gap: 6, maxWidth: "100%", border: "1px solid #cbd5e1", background: "#fff", color: "#0f172a", borderRadius: 999, padding: "5px 9px", cursor: "pointer", fontSize: 12 };
const activeOpenTargetChipStyle = { ...openTargetChipStyle, border: "1px solid #2563eb", background: "#dbeafe", color: "#1d4ed8", fontWeight: 700 };
const emptyIssueStyle = { border: "1px dashed #cbd5e1", borderRadius: 8, padding: 10, color: "#64748b", fontSize: 13, background: "#f8fafc" };
const existingIssueListStyle = { display: "grid", gap: 8 };
const existingIssueCardStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, background: "#fff" };

export default AddIssue;

